<template>
  <component
    :is="(routerLink || href) ? 'LinkElement' : (inline ? 'span' : 'div')"
    :defaultElement="(routerLink || href) ? (inline ? 'span' : 'div') : undefined"
    :routerLink="routerLink"
    :href="href"
    :class="[
      $style.base,
      $style[color],
      $style[size],
      {
        [$style.bold]: bold,
        [$style.center]: center,
        [$style.hasIcon]: !!iconBefore || !!iconAfter || !!href,
        [$style.hasIconBefore]: !!iconBefore || !!href,
        [$style.hasIconAfter]: !!iconAfter,
        [$style.italic]: italic,
        [$style.lineThrough]: lineThrough,
        [$style.underline]: underline,
        [$style.link]: isLink,
        [$style.oneLine]: oneLine,
        [$style.colored]: isColored,
        [$style.hasMaxLines]: !!maxLines
      }
    ]"
    :style="maxLines ? { '-webkit-line-clamp': maxLines, 'line-clamp': maxLines } : undefined"
    :download="(routerLink || href) ? download : undefined"
    v-bind="$attrs.onClick ? { onClick: $attrs.onClick } : {}"
  >
    <div
      v-if="beforeIcon"
      :class="$style.iconBefore"
    >
      <BaseIcon
        :name="beforeIcon"
        :color="iconColor"
        :size="size === 's' ? 'xs' : 's'"
      />
    </div>
    <slot />
    <div
      v-if="iconAfter"
      :class="$style.iconAfter"
    >
      <BaseIcon
        :name="iconAfter"
        :color="iconColor"
        :size="size === 's' ? 'xs' : 's'"
      />
    </div>
  </component>
</template>

<script lang="ts">
import LinkElement from '@/components/_shared/Link.vue';

export default {
  inheritAttrs: false,
  components: {
    LinkElement,
  },
};
</script>

<script
  lang="ts"
  setup
>
import { computed, useAttrs } from 'vue';
import type { LocationQueryRaw } from 'vue-router';
import type { IconName } from '@/components/base-icon/types';

const props = withDefaults(
  defineProps<{
    bold?: boolean;
    center?: boolean;
    color?:
      | 'default'
      | 'primary'
      | 'info'
      | 'inverted'
      | 'success'
      | 'warning'
      | 'error'
      | 'secondary'
      | 'placeholder'
      | 'fixed';
    download?: boolean;
    href?: string;
    iconAfter?: IconName;
    iconBefore?: IconName;
    inline?: boolean;
    italic?: boolean;
    lineThrough?: boolean;
    maxLines?: number;
    underline?: boolean;
    oneLine?: boolean;
    routerLink?: LocationQueryRaw;
    size?: 's' | 'm' | 'l';
  }>(),
  {
    color: 'default',
    size: 'm',
  },
);

const beforeIcon = computed(() => {
  if (props.iconBefore) {
    return props.iconBefore;
  }
  return props.href ? 'external_link' : undefined;
});

const attrs = useAttrs();
const isLink = computed(
  () => !!attrs.onClick || !!props.routerLink || !!props.href,
);
const isColored = computed(
  () =>
    props.color === 'primary' ||
    props.color === 'info' ||
    props.color === 'success' ||
    props.color === 'warning' ||
    props.color === 'error' ||
    props.color === 'inverted',
);
// Coloured text and links colour their icons like the text
const iconColor = computed(() =>
  isColored.value || isLink.value ? 'inherit' : 'default',
);
</script>

<style
  lang="scss"
  module
>
.base {
  font-weight: normal;
  line-height: 1.4;

  a {
    color: var(--link);

    @include hover {
      color: var(--link-hover);
    }
  }

  &.s {
    font-size: 12px;
  }

  &.l {
    font-size: 18px;
  }

  &.bold {
    font-weight: bold;
  }

  &.italic {
    font-style: italic;
  }

  &.lineThrough {
    text-decoration: line-through;
  }

  &.underline {
    text-decoration: underline;
  }

  &.center {
    text-align: center;
  }

  &.primary {
    color: var(--action-primary);
  }

  &.info {
    color: var(--info-dark);
  }

  &.error {
    color: var(--error-dark);
  }

  &.warning {
    color: var(--warning-dark);
  }

  &.success {
    color: var(--success-dark);
  }

  // White in both themes: used on the nav and dark banners, which stay dark
  &.inverted {
    color: var(--color-gray-white);
  }

  &.secondary {
    color: var(--text-secondary);
  }

  &.placeholder {
    color: var(--text-placeholder);
  }

  &.fixed {
    color: var(--text-fixed);
  }

  &.oneLine {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100%;

    br {
      content: '';
      &:after {
        content: ' ';
      }
    }
  }

  &.link {
    display: inline-block;
    cursor: pointer;
    transition: color 0.2s ease;
    max-width: max-content; // Prevents clickable empty space

    &:not(.colored),
    &.primary {
      color: var(--link);

      @include hover {
        color: var(--link-hover);
      }
    }

    &.info {
      @include hover {
        color: var(--info-darker);
      }
    }

    &.error {
      @include hover {
        color: var(--error-darker);
      }
    }

    &.warning {
      @include hover {
        color: var(--warning-darker);
      }
    }

    &.success {
      @include hover {
        color: var(--success-darker);
      }
    }
  }

  &.hasIcon {
    position: relative;
  }

  &.hasIconBefore {
    &.s {
      padding-left: 16px;
    }

    &:not(.s) {
      padding-left: 22px;
    }
  }

  &.hasIconAfter {
    &.s {
      padding-right: 16px;
    }

    &:not(.s) {
      padding-right: 22px;
    }
  }

  &.hasMaxLines {
    display: -webkit-box;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}

.iconBefore,
.iconAfter {
  position: absolute;
  display: flex;
  align-items: center;

  .base.s & {
    top: 1px;
  }

  .base:not(.s) & {
    top: 2px;
  }
}

.iconBefore {
  left: 0;
}

.iconAfter {
  right: 0;
}
</style>
